"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, ShieldCheck, Printer, MonitorCheck, Wrench, ArrowRight, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function Facilities() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const defaultMascot = {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
    alt: "ROBOTO Mascot"
  };
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      } else {
        setLang("vi");
      }
    } catch {
      setLang("vi");
    }
    const handleCustomLangChange = (e: CustomEvent<{
      lang: "vi" | "en";
    }>) => {
      if (e?.detail?.lang && (e.detail.lang === "vi" || e.detail.lang === "en")) {
        setLang(e.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomLangChange as EventListener);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomLangChange as EventListener);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const copy = {
    vi: {
      specRail: "TIÊU CHUẨN PHÒNG LAB",
      activeStatus: "TRẠNG THÁI LAB: ĐÃ HIỆU CHUẨN",
      instructorRatio: "TỶ LỆ 1:6 GIẢNG VIÊN HƯỚNG DẪN",
      metaPodsTitle: "Trạm thực hành",
      metaPodsVal: "Trạm thực hành riêng biệt",
      metaFabTitle: "Chế tác & Tạo mẫu",
      metaFabVal: "In 3D & Cắt Laser",
      metaArenaTitle: "Sàn thi đấu",
      metaArenaVal: "Sàn thi đấu thực hành",
      badge: "Không Gian Thực Hành Chuẩn Mực",
      heading: "Phòng lab Robotics chuyên nghiệp dành riêng cho thế hệ kỹ sư nhí",
      subtitle: "Khám phá không gian thực hành chuẩn quốc tế, nơi các em trực tiếp thử nghiệm phần cứng, tối ưu hóa thuật toán và phát triển tư duy Engineering an toàn tuyệt đối.",
      description: "Học viện ROBOTO nói không với đồ chơi lắp ráp giải trí thông thường; học viên từ 4 đến 16 tuổi được rèn luyện trực tiếp với bộ nguồn thí nghiệm, máy hiện sóng kỹ thuật số, khung nhôm kỹ thuật và trạm Coding màn hình kép. Mỗi không gian làm việc đều được thiết kế công thái học chuẩn xác, nối đất chống tĩnh điện nghiêm ngặt nhằm bảo đảm sự tập trung cao độ và an toàn trọn vẹn trong suốt quá trình nghiên cứu.",
      features: [{
        title: "Quy chuẩn an toàn được chứng nhận",
        desc: "Hệ thống hàn điện áp thấp, máy hút lọc khí nhựa in an toàn không độc hại và đội ngũ giảng viên giám sát liên tục.",
        icon: ShieldCheck
      }, {
        title: "Bộ công cụ phần cứng chuẩn công nghiệp",
        desc: "Trang bị LEGO Education Spike Prime, Arduino, micro:bit cùng khung hợp kim gia công CNC chuyên dụng.",
        icon: Cpu
      }, {
        title: "Khu vực tạo mẫu nhanh chuyên sâu",
        desc: "Máy in 3D buồng kín đùn kép và máy cắt laser được cân chỉnh chuẩn xác cho các chi tiết do học viên tự thiết kế.",
        icon: Printer
      }, {
        title: "Trạm Coding màn hình kép tối ưu",
        desc: "Bàn ghế công thái học linh hoạt, hỗ trợ học viên biên dịch, kiểm thử dữ liệu Python và C++ theo thời gian thực.",
        icon: MonitorCheck
      }],
      ctaTour: "Đăng ký tham quan phòng lab",
      ctaPrograms: "Khám phá chương trình học",
      imgAlt: "Phòng lab Robotics trung tâm với máy in 3D, các trạm lập trình màn hình kép và sàn thi đấu chuẩn FLL"
    },
    en: {
      specRail: "LABORATORY SPECIFICATION",
      activeStatus: "ACTIVE LAB STATUS: CALIBRATED",
      instructorRatio: "RATIO 1:6 INSTRUCTOR LEAD",
      metaPodsTitle: "Workstations",
      metaPodsVal: "Dedicated workstations",
      metaFabTitle: "Fabrication",
      metaFabVal: "Rapid 3D/Laser",
      metaArenaTitle: "Arena Size",
      metaArenaVal: "Practice arena",
      badge: "Physical Environment",
      heading: "Professional robotics labs built for young engineers",
      subtitle: "Step inside spaces designed for real hardware iteration, algorithmic testing, and absolute safety.",
      description: "Our campus eliminates plastic novelty toys in favor of benchtop power supplies, digital oscilloscopes, modular aluminum chassis, and dual-monitor stations. Every station is ergonomically scaled for students aged 4 to 16, ensuring proper posture, strict ESD grounding, and uninterrupted focus during complex builds.",
      features: [{
        title: "Certified safety protocols",
        desc: "Low-voltage soldering stations, non-toxic filament extractors, and constant faculty oversight.",
        icon: ShieldCheck
      }, {
        title: "Industry hardware kits",
        desc: "LEGO Education Spike Prime, Arduino, micro:bit, and custom CNC-milled chassis.",
        icon: Cpu
      }, {
        title: "Rapid prototyping row",
        desc: "Enclosed dual-extrusion 3D printers and laser cutters calibrated for student-designed parts.",
        icon: Printer
      }, {
        title: "Dual-monitor code pods",
        desc: "Adjustable ergonomic workstations where students test real-time Python and C++ telemetry.",
        icon: MonitorCheck
      }],
      ctaTour: "Book a lab tour",
      ctaPrograms: "Explore programs",
      imgAlt: "Main robotics laboratory with 3D printers, dual monitor programming pods, and competition arena"
    }
  };
  const t = copy[lang];
  const motionInitial = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  return <section id="our-facilities" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-24 border-b border-border overflow-hidden">
      {/* Decorative Mascot Floating Visual */}
      <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
      once: true
    }} transition={{
      duration: 0.45,
      ease: "easeOut"
    }} className="absolute top-4 sm:top-6 md:top-8 right-4 sm:right-6 md:right-10 lg:right-16 xl:right-20 pointer-events-none select-none z-10 hidden sm:block" aria-hidden="true">
        <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} loading="lazy" decoding="async" className="w-16 sm:w-20 md:w-24 lg:w-28 xl:w-32 h-auto object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
      </motion.div>

      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20">
        {/* Spec rail accent */}
        <div className="flex items-center gap-2 mb-6">
          <span className="w-1.5 h-1.5 bg-secondary rounded-xs" />
          <div className="w-12 h-px bg-secondary" />
          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            {t.specRail}
          </span>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Visual pane */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-4">
            <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_laboratory_main_facility.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_laboratory_main_facility.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_laboratory_main_facility.png-wvc-sizes" alt={t.imgAlt} loading="lazy" decoding="async" className="w-full h-auto aspect-[16/10] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent pointer-events-none" />

              {/* Status readout badge on the visual with tech-themed circuit accents */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs font-mono text-primary-foreground">
                <div className="relative">
                  {/* Circuit-line decorative detail around active status badge */}
                  <svg className="absolute -top-3.5 -left-2.5 w-14 h-7 text-secondary/40 pointer-events-none" fill="none" viewBox="0 0 56 28" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
                    <path d="M 2 26 V 12 L 14 2 H 44" />
                    <circle cx="44" cy="2" r="1.5" className="fill-secondary/70 stroke-none" />
                    <circle cx="2" cy="26" r="1.5" className="fill-secondary/70 stroke-none" />
                  </svg>
                  <svg className="absolute -bottom-2.5 -right-3 w-12 h-6 text-secondary/30 pointer-events-none" fill="none" viewBox="0 0 48 24" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
                    <path d="M 46 2 H 28 L 18 22 H 4" />
                    <circle cx="46" cy="2" r="1.5" className="fill-secondary/60 stroke-none" />
                    <circle cx="4" cy="22" r="1.5" className="fill-secondary/60 stroke-none" />
                  </svg>

                  <div className="relative z-10 flex items-center gap-2 bg-primary/90 backdrop-blur-xs px-3 py-1.5 rounded-md border border-border/20 shadow-xs">
                    <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                    <span>{t.activeStatus}</span>
                  </div>
                </div>

                <div className="relative hidden sm:block">
                  {/* Circuit-line decorative detail around instructor ratio badge */}
                  <svg className="absolute -top-3.5 -right-2.5 w-14 h-7 text-secondary/40 pointer-events-none" fill="none" viewBox="0 0 56 28" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
                    <path d="M 54 26 V 12 L 42 2 H 12" />
                    <circle cx="12" cy="2" r="1.5" className="fill-secondary/70 stroke-none" />
                    <circle cx="54" cy="26" r="1.5" className="fill-secondary/70 stroke-none" />
                  </svg>

                  <div className="relative z-10 flex items-center gap-1.5 bg-primary/90 backdrop-blur-xs px-3 py-1.5 rounded-md border border-border/20 shadow-xs">
                    <Layers className="w-3.5 h-3.5 text-secondary" />
                    <span>{t.instructorRatio}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick architectural metadata strip */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-muted rounded-lg border border-border">
                <p className="font-mono text-xs uppercase text-muted-foreground">{t.metaPodsTitle}</p>
                <p className="font-serif font-bold text-foreground text-base sm:text-lg">{t.metaPodsVal}</p>
              </div>
              <div className="p-3 bg-muted rounded-lg border border-border">
                <p className="font-mono text-xs uppercase text-muted-foreground">{t.metaFabTitle}</p>
                <p className="font-serif font-bold text-foreground text-base sm:text-lg">{t.metaFabVal}</p>
              </div>
              <div className="p-3 bg-muted rounded-lg border border-border">
                <p className="font-mono text-xs uppercase text-muted-foreground">{t.metaArenaTitle}</p>
                <p className="font-serif font-bold text-foreground text-base sm:text-lg">{t.metaArenaVal}</p>
              </div>
            </div>
          </motion.div>

          {/* Descriptive column */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut",
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-5 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 font-mono text-xs uppercase font-semibold text-secondary">
                <Wrench className="w-3.5 h-3.5" />
                {t.badge}
              </span>
            </div>

            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight mb-4">
              {t.heading}
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-4">
              {t.subtitle}
            </p>

            <p className="text-foreground/90 text-sm sm:text-base leading-relaxed mb-6">
              {t.description}
            </p>

            {/* Feature matrix */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {t.features.map((feat, i) => {
              const Icon = feat.icon;
              return <Card key={feat.title} data-index={i} className="p-3.5 gap-2 border border-border bg-card rounded-lg shadow-2xs hover:border-secondary transition-[border-color,box-shadow]">
                    <CardContent className="p-0 flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        {/* Circular frame with subtle primary→accent gradient border */}
                        <div className="p-[1.5px] rounded-full bg-gradient-to-br from-primary/70 via-primary/30 to-accent/70 shrink-0 shadow-2xs">
                          <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                        </div>
                        <h3 className="font-serif text-sm font-semibold text-foreground leading-snug">
                          {feat.title}
                        </h3>
                      </div>
                      <p className="text-xs text-muted-foreground leading-snug pl-0.5">
                        {feat.desc}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Primary actions */}
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm font-semibold">
                <Link to="/FreeTrialContactPage">
                  {t.ctaTour}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to="/ProgramsPage">
                  {t.ctaPrograms}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}